import { useState, useEffect } from 'react'
import { motion, AnimatePresence, PanInfo } from 'framer-motion'
import { EDUCATION_SLIDES, PROGRESS_MESSAGES } from '@/data/educationContent'

interface EducationalLoaderProps {
  progress: number // 0-100
  isComplete?: boolean
  onComplete?: () => void
}

// Decorative sparkle component
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Floating bubble for background
const FloatingBubble = ({
  size,
  color,
  left,
  delay
}: {
  size: number
  color: string
  left: string
  delay: number
}) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      backgroundColor: color,
      left,
      bottom: -20
    }}
    animate={{
      y: [0, -800],
      opacity: [0, 0.4, 0.4, 0]
    }}
    transition={{
      duration: 10 + Math.random() * 5,
      delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  />
)

export function EducationalLoader({ progress, isComplete = false, onComplete }: EducationalLoaderProps) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slideProgress, setSlideProgress] = useState(0)

  // Get current progress message
  const progressMessage =
    [...PROGRESS_MESSAGES].reverse().find((m) => progress >= m.progress)?.message ||
    'Preparing your analysis...'

  // Rotate slides
  useEffect(() => {
    if (isComplete) return

    const slide = EDUCATION_SLIDES[currentSlide]
    const interval = setInterval(() => {
      setSlideProgress((prev) => {
        const increment = (100 / slide.duration) * 100 // Update every 100ms
        if (prev + increment >= 100) {
          setCurrentSlide((current) => (current + 1) % EDUCATION_SLIDES.length)
          return 0
        }
        return prev + increment
      })
    }, 100)

    return () => clearInterval(interval)
  }, [currentSlide, isComplete])

  // Handle completion
  useEffect(() => {
    if (isComplete && onComplete) {
      const timer = setTimeout(onComplete, 500)
      return () => clearTimeout(timer)
    }
  }, [isComplete, onComplete])

  const slide = EDUCATION_SLIDES[currentSlide]

  // Bubble colors
  const bubbles = [
    { size: 16, color: '#FFB6C1', left: '10%', delay: 0 },
    { size: 12, color: '#87CEEB', left: '25%', delay: 2 },
    { size: 20, color: '#98FB98', left: '45%', delay: 1 },
    { size: 14, color: '#DDA0DD', left: '65%', delay: 3 },
    { size: 18, color: '#F0E68C', left: '80%', delay: 1.5 },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#FFFEF1] to-[#F0FAFA] flex flex-col items-center justify-center px-6 pb-24 relative overflow-hidden safe-bottom">
      {/* Floating bubbles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {bubbles.map((bubble, i) => (
          <FloatingBubble key={i} {...bubble} />
        ))}
      </div>

      {/* Decorative sparkles */}
      <Sparkle className="absolute top-16 left-6" size={18} color="#FF8C33" delay={0} />
      <Sparkle className="absolute top-24 right-8" size={14} color="#46C0C6" delay={0.5} />
      <Sparkle className="absolute top-40 left-12" size={12} color="#F596FA" delay={0.8} />
      <Sparkle className="absolute bottom-40 right-10" size={16} color="#7182F8" delay={0.3} />
      <Sparkle className="absolute bottom-28 left-8" size={10} color="#4CD964" delay={0.6} />

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Progress Ring */}
        <motion.div
          role="status"
          aria-label={`Analysis progress: ${Math.round(progress)}%`}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative w-36 h-36 mx-auto mb-6"
        >
          {/* Glow effect - lighter */}
          <div className="absolute inset-0 rounded-full bg-[#46C0C6]/10 blur-xl" />

          <svg className="w-full h-full transform -rotate-90 relative z-10" viewBox="0 0 100 100">
            {/* Background circle - lighter */}
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="#E8F4F4"
              strokeWidth="6"
            />
            {/* Progress circle - thinner */}
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${progress * 2.83} 283`}
              className="transition-all duration-500"
            />
            {/* Gradient definition - softer colors */}
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#7DD3D8" />
                <stop offset="100%" stopColor="#A3B8F8" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.span
              key={Math.round(progress)}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              className="text-3xl font-bold text-[#075D61]"
              style={{ fontFamily: "'Anybody', sans-serif" }}
            >
              {Math.round(progress)}%
            </motion.span>
          </div>
        </motion.div>

        {/* Progress Message */}
        <motion.p
          key={progressMessage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[#075D61] text-center mb-8 font-medium"
        >
          {progressMessage}
        </motion.p>

        {/* Education Slide - Swipeable */}
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.3}
            onDragEnd={(_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
              // Swipe left = next, swipe right = previous
              if (info.offset.x < -50 || info.velocity.x < -300) {
                setCurrentSlide((current) => (current + 1) % EDUCATION_SLIDES.length)
                setSlideProgress(0)
              } else if (info.offset.x > 50 || info.velocity.x > 300) {
                setCurrentSlide((current) => (current - 1 + EDUCATION_SLIDES.length) % EDUCATION_SLIDES.length)
                setSlideProgress(0)
              }
            }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 cursor-grab active:cursor-grabbing"
          >
            {/* Slide Header */}
            <div className="flex items-center gap-3 mb-4">
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="text-4xl"
              >
                {slide.icon}
              </motion.span>
              <h3
                className="text-xl font-bold text-[#075D61]"
                style={{ fontFamily: "'Anybody', sans-serif" }}
              >
                {slide.title}
              </h3>
            </div>

            {/* Slide Content */}
            <p className="text-gray-600 leading-relaxed mb-4">{slide.content}</p>

            {/* Tip */}
            {slide.tip && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-r from-[#46C0C6]/10 to-[#7182F8]/10 rounded-2xl px-4 py-3"
              >
                <p className="text-sm text-[#075D61] font-medium">
                  <span className="mr-2">💡</span>
                  {slide.tip}
                </p>
              </motion.div>
            )}

            {/* Slide Progress */}
            <div className="mt-4 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#46C0C6] to-[#7182F8]"
                initial={{ width: 0 }}
                animate={{ width: `${slideProgress}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-2 mt-5">
          {EDUCATION_SLIDES.map((_, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                setCurrentSlide(index)
                setSlideProgress(0)
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentSlide
                  ? 'bg-gradient-to-r from-[#46C0C6] to-[#7182F8] w-6'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Skip hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-xs text-gray-400 text-center mt-6"
        >
          ← Swipe or tap dots to browse tips →
        </motion.p>
      </div>
    </div>
  )
}
